import { EButtonIntents } from '@src/typings';
import { buttonVariants } from '@src/utilities/buttonVariants';
import React from 'react';
import { useTheme, withTheme } from '../ThemeProvider';
import { StyledButton } from './styles';
import { ButtonProps } from './types';

function ButtonComponent({
    intent,
    size,
    rounded,
    display,
    className,
    children,
    StartAdornment: StartAdornment,
    EndAdornment: EndAdornment,
    style,
    ...props
}: ButtonProps) {
    const { theme } = useTheme();

    // Define style overrides based on intent
    const getIntentStyles = () => {
        const styles: React.CSSProperties = {};

        switch (intent) {
            case EButtonIntents.Filled:
                styles.backgroundColor = theme.colors.primary.main.filled;
                styles.color = theme.colors.primary.main.text;
                styles.borderColor = 'transparent';
                break;
            case EButtonIntents.LightFilled:
                styles.backgroundColor = theme.colors.primary.light.filled;
                styles.color = theme.colors.primary.light.text;
                styles.borderColor = 'transparent';
                break;
            case EButtonIntents.Linear:
                styles.backgroundColor = theme.colors.primary.linear.filled;
                styles.color = theme.colors.primary.linear.text;
                styles.borderColor = theme.colors.primary.linear.border;
                break;
            case EButtonIntents.LightLinear:
                styles.backgroundColor =
                    theme.colors.primary.linear.lightFilled;
                styles.color = theme.colors.primary.linear.lightText;
                styles.borderColor = theme.colors.primary.linear.lightBorder;
                break;
            case EButtonIntents.Text:
                styles.backgroundColor = 'transparent';
                styles.color = theme.colors.primary.main.filled;
                styles.borderColor = 'transparent';
                styles.boxShadow = 'none';
                break;
            default:
                styles.backgroundColor = theme.colors.primary.main.filled;
                styles.color = theme.colors.primary.main.text;
                styles.borderColor = 'transparent';
                break;
        }

        return styles;
    };

    const intentStyles = getIntentStyles();

    return (
        <StyledButton
            className={buttonVariants({
                className,
                intent,
                rounded,
                size,
                display,
                _content:
                    StartAdornment || EndAdornment
                        ? display === 'justifyBetween'
                            ? 'textAndIconJustify'
                            : 'textAndIcon'
                        : 'text',
            })}
            style={{ ...intentStyles, ...style }}
            {...props}
        >
            {StartAdornment ? (
                <StartAdornment
                    className="-ml-0.4 h-4 w-4"
                    aria-hidden="true"
                    style={{ color: 'inherit' }}
                />
            ) : null}
            {children}
            {EndAdornment ? (
                <EndAdornment
                    className="-mr-0.4 h-4 w-4"
                    aria-hidden="true"
                    style={{ color: 'inherit' }}
                />
            ) : null}
        </StyledButton>
    );
}

ButtonComponent.displayName = 'ButtonComponent';

export const Button = withTheme(ButtonComponent);
